import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Percent, DollarSign, Calendar, Hash, Trash2, Edit } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../App';
import { getPromoCodes, createPromoCode, updatePromoCode, deletePromoCode } from '../../api/promoCodes';

const AdminPromoCodes = () => {
  const { token } = useAuth();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    min_order_amount: 0,
    max_uses: 0,
    valid_from: '',
    valid_until: '',
    is_active: true
  });

  const fetchCodes = useCallback(async () => {
    try {
      const data = await getPromoCodes(token);
      setCodes(data);
    } catch (error) {
      console.error('Error fetching promo codes:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const resetForm = () => {
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: 10,
      min_order_amount: 0,
      max_uses: 0,
      valid_from: '',
      valid_until: '',
      is_active: true
    });
    setSelectedCode(null);
  };

  const handleOpenModal = (code = null) => {
    if (code) {
      setSelectedCode(code);
      setFormData({
        code: code.code,
        discount_type: code.discount_type,
        discount_value: code.discount_value,
        min_order_amount: code.min_order_amount || 0,
        max_uses: code.max_uses || 0,
        valid_from: code.valid_from ? code.valid_from.split('T')[0] : '',
        valid_until: code.valid_until ? code.valid_until.split('T')[0] : '',
        is_active: code.is_active
      });
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        valid_from: formData.valid_from ? new Date(formData.valid_from).toISOString() : null,
        valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null
      };

      if (selectedCode) {
        await updatePromoCode(token, selectedCode.id, payload);
      } else {
        await createPromoCode(token, payload);
      }
      fetchCodes();
      setIsModalOpen(false);
      resetForm();
      setMessage({ type: 'success', text: selectedCode ? 'Kod zaktualizowany' : 'Kod dodany' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Błąd podczas zapisywania' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten kod promocyjny?')) return;

    try {
      await deletePromoCode(token, id);
      fetchCodes();
      setMessage({ type: 'success', text: 'Kod usunięty' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Błąd podczas usuwania' });
    }
  };

  const handleToggleActive = async (code) => {
    try {
      await updatePromoCode(token, code.id, { is_active: !code.is_active });
      fetchCodes();
    } catch (error) {
      setMessage({ type: 'error', text: 'Błąd' });
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Kody Promocyjne">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Kody Promocyjne">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-white">Kody Promocyjne</h1>
            <p className="text-zinc-400 mt-1">Zarządzaj rabatami i promocjami</p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors flex items-center gap-2"
            data-testid="add-promo-code-btn"
          >
            <Plus size={18} />
            Dodaj kod
          </button>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
            {message.text}
          </div>
        )}

        {/* Codes Table */}
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-zinc-900/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-zinc-400">Kod</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-zinc-400">Rabat</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-zinc-400">Min. kwota</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-zinc-400">Użycia</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-zinc-400">Ważność</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-zinc-400">Status</th>
                <th className="px-6 py-4 text-right text-sm font-medium text-zinc-400">Akcje</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-700">
              {codes.map((code) => (
                <tr key={code.id} className="hover:bg-zinc-800/30" data-testid={`promo-row-${code.id}`}>
                  <td className="px-6 py-4">
                    <span className="font-mono text-lg font-bold text-emerald-400">{code.code}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {code.discount_type === 'percentage' ? (
                        <>
                          <Percent size={16} className="text-zinc-400" />
                          <span className="text-white">{code.discount_value}%</span>
                        </>
                      ) : (
                        <>
                          <DollarSign size={16} className="text-zinc-400" />
                          <span className="text-white">{code.discount_value} zł</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-zinc-300">
                    {code.min_order_amount > 0 ? `${code.min_order_amount} zł` : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-zinc-300">
                      {code.used_count} / {code.max_uses > 0 ? code.max_uses : '∞'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-zinc-400">
                    {code.valid_until ? (
                      <div className="flex items-center gap-1">
                        <Calendar size={14} />
                        do {new Date(code.valid_until).toLocaleDateString('pl-PL')}
                      </div>
                    ) : (
                      'Bezterminowo'
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleToggleActive(code)}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        code.is_active
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-zinc-700 text-zinc-400'
                      }`}
                      data-testid={`toggle-code-${code.id}`}
                    >
                      {code.is_active ? 'Aktywny' : 'Nieaktywny'}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleOpenModal(code)}
                        className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded"
                        data-testid={`edit-code-${code.id}`}
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(code.id)}
                        className="p-2 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded"
                        data-testid={`delete-code-${code.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {codes.length === 0 && (
            <div className="text-center py-12 text-zinc-400">
              Brak kodów promocyjnych. Kliknij "Dodaj kod" aby utworzyć pierwszy.
            </div>
          )}
        </div>

        {/* Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 border border-zinc-700 rounded-lg w-full max-w-lg">
              <div className="p-6">
                <h2 className="text-xl font-bold text-white mb-6">
                  {selectedCode ? 'Edytuj kod promocyjny' : 'Nowy kod promocyjny'}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Kod *</label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      required
                      placeholder="np. RABAT20"
                      className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white font-mono uppercase focus:border-emerald-500 focus:outline-none"
                      data-testid="promo-code-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">Typ rabatu</label>
                      <select
                        value={formData.discount_type}
                        onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                        className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                        data-testid="discount-type-select"
                      >
                        <option value="percentage">Procentowy (%)</option>
                        <option value="fixed">Kwotowy (zł)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">
                        Wartość {formData.discount_type === 'percentage' ? '(%)' : '(zł)'}
                      </label>
                      <input
                        type="number"
                        value={formData.discount_value}
                        onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) })}
                        min="0"
                        max={formData.discount_type === 'percentage' ? 100 : 999999}
                        step={formData.discount_type === 'percentage' ? 1 : 0.01}
                        className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                        data-testid="discount-value-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">Min. kwota zamówienia</label>
                      <input
                        type="number"
                        value={formData.min_order_amount}
                        onChange={(e) => setFormData({ ...formData, min_order_amount: parseFloat(e.target.value) })}
                        min="0"
                        step="0.01"
                        placeholder="0 = bez limitu"
                        className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">Limit użyć</label>
                      <input
                        type="number"
                        value={formData.max_uses}
                        onChange={(e) => setFormData({ ...formData, max_uses: parseInt(e.target.value) })}
                        min="0"
                        placeholder="0 = bez limitu"
                        className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">Ważny od</label>
                      <input
                        type="date"
                        value={formData.valid_from}
                        onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                        className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">Ważny do</label>
                      <input
                        type="date"
                        value={formData.valid_until}
                        onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                        className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-emerald-500 focus:ring-emerald-500"
                    />
                    <label htmlFor="is_active" className="text-zinc-300">Kod aktywny</label>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => { setIsModalOpen(false); resetForm(); }}
                      className="flex-1 px-4 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600"
                    >
                      Anuluj
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex-1 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"
                      data-testid="save-promo-code-btn"
                    >
                      {saving ? 'Zapisywanie...' : 'Zapisz'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPromoCodes;
